// FILE LOCATION: frontend/src/view/prospecting/ProspectingUniverse.jsx

import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import Select from "react-select";
import toastr from "../../utils/toast";
import ExecutivesModalProspecting from "./components/ExecutivesModalProspecting";
import AddProspect from "./components/AddProspect";
import ExistingClient from "./components/ExistingClient";

export default function ProspectingUniverse() {
  const [step, setStep] = useState(1);
  const [search, setSearch] = useState("");
  const [searchCountry, setSearchCountry] = useState("");
  const [dataindustries, setDataIndustries] = useState([]);
  const [dataregions, setDataRegions] = useState([]);
  const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const PINE_BASE_URL = process.env.REACT_APP_PINE_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const [updatedIndustries, setUpdatedIndustries] = useState([]);
  const [updatedCountries, setUpdatedCountries] = useState([]);
  const [companyRevenue, setCompanyRevenue] = useState([]);
  const [companySizes, setCompanySizes] = useState([]);
  const [addProspect, setAddProspect] = useState(false);
  const [addProspectCompany, setAddProspectCompany] = useState(null);
  const [ShowExistingClient, setShowExistingClient] = useState(false);
  const [ExistingClientId, setExistingClientId] = useState(null);

  // Companies
  const [companies, setCompanies] = useState([]);
  const [totalCompanies, setTotalCompanies] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Filters
  const [revenueFilter, setRevenueFilter] = useState([]);
  const [sizeFilter, setSizeFilter] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [companyInput, setCompanyInput] = useState("");
  const [companyNames, setCompanyNames] = useState([]);

  // Selected companies (full objects for resume)
  const [selectedCompanies, setSelectedCompanies] = useState([]);

  // Search company state for pinning
  const [preSearchCompanies, setPreSearchCompanies] = useState([]);
  const [isSearchActive, setIsSearchActive] = useState(false);

  // Executives modal
  const [executivesModal, setExecutivesModal] = useState(null); // { fqdn, name }

  // Save search modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveSearchName, setSaveSearchName] = useState("");
  const [savingSearch, setSavingSearch] = useState(false);

  // Saved searches modal
  const [showSavedSearchesModal, setShowSavedSearchesModal] = useState(false);
  const [savedSearches, setSavedSearches] = useState([]);
  const [savedSearchesLoading, setSavedSearchesLoading] = useState(false);
  const [isResumed, setIsResumed] = useState(false);

  // Role description
  const [generatedRoleDescription, setGeneratedRoleDescription] = useState("");
  const [JdLoader, setJdLoader] = useState(false);

  const common_api_ref = useRef(false);

  const {
    register,
    handleSubmit,
    trigger,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    reValidateMode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      industries: [],
      countries: [],
    },
  });

  const selectedIndustries = watch("industries");
  const selectedCountries = watch("countries");

  // ─── API Calls ────────────────────────────────────────────────────────────

  const industry_master = () => {
    fetch(`${REACT_APP_API_BASE_URL}/lusha/lusha_industries`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => setDataIndustries(data))
      .catch(console.error);
  };

  const region_country = () => {
    fetch(`${REACT_APP_API_BASE_URL}/lusha/country_region_map`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const regionWise = data.reduce((acc, curr) => {
          const region = curr.region_name;
          if (!acc[region]) acc[region] = [];
          acc[region].push({
            country_id: curr.country_id,
            country: curr.country,
            country_code: curr.country_code,
          });
          return acc;
        }, {});
        setDataRegions(regionWise);
      })
      .catch(console.error);
  };

  const company_filters = async () => {
    try {
      const response = await Promise.all([
        axios.get(`${REACT_APP_API_BASE_URL}/lusha/company_filter_revenue`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${REACT_APP_API_BASE_URL}/lusha/company_filter_sizes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setCompanyRevenue(response[0].data);
      setCompanySizes(response[1].data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (common_api_ref.current) return;
    common_api_ref.current = true;
    industry_master();
    company_filters();
    region_country();
  }, []);

  // ─── Industry Helpers ─────────────────────────────────────────────────────

  const industriesTree = useMemo(() => {
    if (!dataindustries?.length) return [];
    const map = {};
    dataindustries.forEach((item) => {
      const group = item.name;
      const main = item.main_industry;
      if (!map[group]) map[group] = { label: group, mains: {} };
      if (!map[group].mains[main])
        map[group].mains[main] = { label: main, subs: [] };
      map[group].mains[main].subs.push({
        id: item.lusha_industry_id,
        label: item.sub_industry,
      });
    });
    return Object.values(map).map((g) => ({
      ...g,
      mains: Object.values(g.mains),
    }));
  }, [dataindustries]);

  const filteredIndustries = useMemo(() => {
    if (!search) return industriesTree;
    const q = search.toLowerCase();
    return industriesTree
      .map((group) => {
        if (group.label.toLowerCase().includes(q)) return group;
        const mains = group.mains
          .map((main) => {
            if (main.label.toLowerCase().includes(q)) return main;
            const subs = main.subs.filter((s) =>
              s.label.toLowerCase().includes(q),
            );
            return subs.length ? { ...main, subs } : null;
          })
          .filter(Boolean);
        return mains.length ? { ...group, mains } : null;
      })
      .filter(Boolean);
  }, [search, industriesTree]);

  const industryIdMap = useMemo(() => {
    const map = {};
    industriesTree.forEach((g) =>
      g.mains.forEach((m) =>
        m.subs.forEach((s) => {
          map[s.id] = s.label;
        }),
      ),
    );
    return map;
  }, [industriesTree]);

  const getAllSubsFromMains = (mains = []) =>
    mains.flatMap((m) => m.subs || []);
  const isAllSelected = (subs) =>
    subs.every((s) => selectedIndustries?.includes(s.id));
  const isAllMajorSelected = (mains) => {
    const allSubs = getAllSubsFromMains(mains);
    return allSubs.length > 0 && isAllSelected(allSubs);
  };
  const getSelectedCount = (subs = []) =>
    subs.filter((s) => selectedIndustries?.includes(s.id)).length;

  const toggle = (value) => {
    const current = selectedIndustries || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setValue("industries", updated, { shouldValidate: true });
    setUpdatedIndustries(updated);
    // Reset companies when industries change
    resetCompaniesState();
    setIsResumed(false);
  };

  const toggleMain = (subs) => {
    const ids = subs.map((s) => s.id);
    const current = selectedIndustries || [];
    const allSelected = ids.every((id) => current.includes(id));
    const updated = allSelected
      ? current.filter((id) => !ids.includes(id))
      : Array.from(new Set([...current, ...ids]));
    setValue("industries", updated, { shouldValidate: true });
    setUpdatedIndustries(updated);
    resetCompaniesState();
    setIsResumed(false);
  };

  const toggleMajor = (mains) => {
    const allSubs = getAllSubsFromMains(mains);
    toggleMain(allSubs);
  };

  // ─── Country Helpers ──────────────────────────────────────────────────────

  const toggleCountries = (value) => {
    const current = selectedCountries || [];
    const updated = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    setValue("countries", updated, { shouldValidate: true });
    setUpdatedCountries(updated);
    // Reset companies when countries change
    resetCompaniesState();
    setIsResumed(false);
  };

  const toggleRegion = (regionName) => {
    const regionCountries =
      dataregions[regionName]?.map((c) => c.country) || [];
    const current = selectedCountries || [];
    const allSelected = regionCountries.every((c) => current.includes(c));
    const updated = allSelected
      ? current.filter((c) => !regionCountries.includes(c))
      : Array.from(new Set([...current, ...regionCountries]));
    setValue("countries", updated, { shouldValidate: true });
    setUpdatedCountries(updated);
    resetCompaniesState();
    setIsResumed(false);
  };

  const filteredCountriesByRegion = Object.entries(dataregions || {})
    .map(([regionName, countries]) => {
      const filtered = countries.filter((item) =>
        item.country.toLowerCase().includes(searchCountry.toLowerCase()),
      );
      return {
        region: regionName,
        countries: filtered.map((item) => ({
          value: item.country_id,
          label: item.country,
          code: item.country_code,
        })),
      };
    })
    .filter((r) => r.countries.length > 0);

  // ─── Company helpers ──────────────────────────────────────────────────────

  const resetCompaniesState = () => {
    setCompanies([]);
    setSelectedCompanies([]);
    setTotalCompanies(0);
    setCurrentPage(0);
    setCompanyNames([]);
    setCompanyInput("");
    setAppliedFilters({});
    setRevenueFilter([]);
    setSizeFilter([]);
    setIsSearchActive(false);
    setPreSearchCompanies([]);
  };

  const formatAmount = (num) => {
    if (!num) return "";
    if (num >= 1_000_000_000)
      return `$${(num / 1_000_000_000).toString().replace(/\.0$/, "")}B`;
    if (num >= 1_000_000)
      return `$${(num / 1_000_000).toString().replace(/\.0$/, "")}M`;
    if (num >= 1_000)
      return `$${(num / 1_000).toString().replace(/\.0$/, "")}K`;
    return `$${num}`;
  };

  const cr_options = companyRevenue.map((item) => {
    const minLabel = formatAmount(item.min);
    const maxLabel = item.max ? formatAmount(item.max) : null;
    return {
      value: `${item.min}-${item.max || ""}`,
      label: maxLabel ? `${minLabel} - ${maxLabel}` : `${minLabel}+`,
    };
  });

  const sz_options = companySizes.map((item) => ({
    value: `${item.min}-${item.max || ""}`,
    label: `${item.min}${item.max ? ` - ${item.max}` : "+"}`,
  }));

  const get_companies = async (pageNo = 0, filters = {}) => {
    if (loading) return;
    try {
      setLoading(true);
      const response = await axios.post(
        `${REACT_APP_API_BASE_URL}/lusha/company_search`,
        {
          pages: { page: pageNo, size: 50 },
          filters: {
            industries: updatedIndustries,
            revenues: filters.revenues || [],
            size: filters.size || [],
            names: filters.names || [],
          },
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const result = response.data?.data || [];
      setCompanies(result);
      setTotalCompanies(response.data?.totalResults || 0);
      setCurrentPage(pageNo);
    } catch (err) {
      console.error(err);
      toastr.error("Company search failed");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const filters = { revenues: revenueFilter, size: sizeFilter };
    setAppliedFilters(filters);
    get_companies(0, filters);
  };

  const resetFilters = () => {
    setAppliedFilters({});
    setRevenueFilter([]);
    setSizeFilter([]);
    get_companies(0, {});
  };

  const handleRevenueChange = (options) =>
    setRevenueFilter(options?.map((o) => o.value) || []);
  const handleSizeChange = (options) =>
    setSizeFilter(options?.map((o) => o.value) || []);

  const toggleCompanySelection = (company) => {
    setSelectedCompanies((prev) => {
      const exists = prev.find((c) => c.id === company.id);
      return exists
        ? prev.filter((c) => c.id !== company.id)
        : [...prev, company];
    });
  };

  const isCompanySelected = (company) =>
    selectedCompanies.some((c) => c.id === company.id);

  // Company search (name input) — pin selected at top
  const handleCompanySearch = () => {
    if (!companyInput.trim()) {
      // Clear search — restore pre-search state
      setIsSearchActive(false);
      setCompanies(preSearchCompanies);
      setCompanyNames([]);
      return;
    }
    const names = companyInput
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);
    if (!names.length) return;

    if (!isSearchActive) {
      setPreSearchCompanies(companies);
    }
    setIsSearchActive(true);
    setCompanyNames(names);

    const updatedFilters = { ...appliedFilters, names };
    setAppliedFilters(updatedFilters);
    get_companies(0, updatedFilters);
  };

  const clearCompanySearch = () => {
    setCompanyInput("");
    setIsSearchActive(false);
    setCompanyNames([]);
    setCompanies(preSearchCompanies);
    const updatedFilters = { ...appliedFilters };
    delete updatedFilters.names;
    setAppliedFilters(updatedFilters);
  };

  const removeCompanyName = (name) => {
    const updatedNames = companyNames.filter((n) => n !== name);
    setCompanyNames(updatedNames);
    if (!updatedNames.length) {
      clearCompanySearch();
      return;
    }
    const updatedFilters = { ...appliedFilters, names: updatedNames };
    setAppliedFilters(updatedFilters);
    get_companies(0, updatedFilters);
  };

  // Paginate
  const totalPages = Math.ceil(totalCompanies / 50);

  const handlePageChange = (newPage) => {
    get_companies(newPage, appliedFilters);
  };

  // ─── Step navigation ──────────────────────────────────────────────────────

  const steps = [
    {
      id: 1,
      label: "Industries",
      icon: "fa-solid fa-building",
      fields: ["industries"],
    },
    {
      id: 2,
      label: "Geographies",
      icon: "fa-solid fa-globe",
      fields: ["countries"],
    },
    { id: 3, label: "Companies", icon: "fa-solid fa-briefcase", fields: [] },
  ];

  const goToStep = async (nextStep) => {
    const currentFields = steps.find((s) => s.id === step)?.fields;
    const valid = await trigger(currentFields);
    if (valid) {
      if (nextStep === 3 && step !== 3) {
        if (!isResumed) {
          get_companies(0, appliedFilters);
        }
      }
      setStep(nextStep);
    }
    window.scrollTo(0, 0);
  };

  // ─── Suggest Industries (role description required) ───────────────────────

  const suggestIndustries = () => {
    if (!generatedRoleDescription.trim()) {
      toastr.error("Please enter a role description first.");
      return;
    }
    setJdLoader(true);
    axios
      .post(
        `${REACT_APP_API_BASE_URL}/lusha/bd_search_universe_suggest_industries`,
        { role_description: generatedRoleDescription },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((resp) => {
        const {
          industries: suggestedIndustries = [],
          countries: suggestedCountries = [],
        } = resp.data;
        if (suggestedIndustries.length > 0) {
          const updatedIndustries = [...new Set([...suggestedIndustries])];
          setValue("industries", updatedIndustries, { shouldValidate: true });
          setUpdatedIndustries(updatedIndustries);
        }
        if (suggestedCountries.length > 0) {
          const updatedCountries = [...new Set([...suggestedCountries])];
          setValue("countries", updatedCountries, { shouldValidate: true });
          setUpdatedCountries(updatedCountries);
          // Reset companies because geographies changed
          resetCompaniesState();
        }

        // ── Toast feedback ────────────────────────────────────────────────
        const industryMsg =
          suggestedIndustries.length > 0
            ? `${suggestedIndustries.length} industries`
            : null;
        const countryMsg =
          suggestedCountries.length > 0
            ? `${suggestedCountries.length} countries`
            : null;
        const parts = [industryMsg, countryMsg].filter(Boolean);
        if (parts.length > 0) {
          toastr.success(
            `Auto-selected ${parts.join(" and ")} based on your description.`,
          );
        } else {
          toastr.warning(
            "No industries or geographies could be identified from your description.",
          );
        }
      })
      .catch((err) => {
        console.error(err);
        toastr.error("Failed to get suggestions. Please try again.");
      })
      .finally(() => setJdLoader(false));
  };

  // ─── Save Search ──────────────────────────────────────────────────────────

  const openSaveModal = () => {
    if (selectedIndustries.length === 0) {
      toastr.error("Please select at least one industry before saving.");
      return;
    }
    if (selectedCountries.length === 0) {
      toastr.error("Please select at least one geography before saving.");
      return;
    }
    setSaveSearchName("");
    setShowSaveModal(true);
  };

  const handleSaveSearch = async () => {
    if (!saveSearchName.trim()) {
      toastr.error("Please enter a name for this search.");
      return;
    }
    // Save checked companies; if none checked, save all currently displayed
    const toSave = selectedCompanies.length > 0 ? selectedCompanies : companies;
    try {
      setSavingSearch(true);
      await axios.post(
        `${REACT_APP_API_BASE_URL}/prospecting/save_search`,
        {
          name: saveSearchName.trim(),
          description: generatedRoleDescription,
          industries: selectedIndustries,
          countries: selectedCountries,
          selected_companies: toSave,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toastr.success("Search saved successfully.");
      setShowSaveModal(false);
    } catch (err) {
      if (err.response?.status === 409) {
        toastr.error(
          "A saved search with this name already exists. Please choose a different name.",
        );
      } else {
        toastr.error("Failed to save search. Please try again.");
      }
    } finally {
      setSavingSearch(false);
    }
  };

  // ─── Saved Searches (Resume) ──────────────────────────────────────────────

  const openSavedSearches = async () => {
    setShowSavedSearchesModal(true);
    setSavedSearchesLoading(true);
    try {
      const resp = await axios.get(
        `${REACT_APP_API_BASE_URL}/prospecting/my_searches`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSavedSearches(resp.data || []);
    } catch (err) {
      toastr.error("Failed to load Saved Target List.");
    } finally {
      setSavedSearchesLoading(false);
    }
  };

  const resumeSearch = (saved) => {
    // Restore description
    setGeneratedRoleDescription(saved.description || "");

    // Restore industries
    const inds = saved.industries || [];
    setValue("industries", inds, { shouldValidate: true });
    setUpdatedIndustries(inds);

    // Restore countries
    const cnts = saved.countries || [];
    setValue("countries", cnts, { shouldValidate: true });
    setUpdatedCountries(cnts);

    // Restore selected companies
    const savedComps = saved.selected_companies || [];
    setSelectedCompanies(savedComps);
    setCompanies(savedComps);
    setTotalCompanies(savedComps.length);

    setIsResumed(true);
    setShowSavedSearchesModal(false);
    setStep(3);
    window.scrollTo(0, 0);
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  // When search is active: selected pinned at top, then search results (excluding already selected)
  const displayedCompanies = useMemo(() => {
    if (!isSearchActive) return companies;
    const searchResults = companies.filter((c) => !isCompanySelected(c));
    return [...selectedCompanies, ...searchResults];
  }, [isSearchActive, companies, selectedCompanies]);

  return (
    <>
      <style>{`
        .accordion-button::after { display: none; }
        .width-100 { width: 100px !important; }
        .cursor-pointer { cursor: pointer; }
      `}</style>

      <div className="content">
        <div
          className="card"
          style={{
            background:
              "radial-gradient(circle, #f265229c 0%, rgba(0, 0, 0, 0) 70%)",
          }}
        >
          <div className="card-body">
            <div className="text-center mb-3">
              <h1 style={{ fontSize: "xxx-large" }}>
                Build Your <span className="text-primary">Target Universe</span>
              </h1>
              <p className="fs-18">
                Describe who you want to target and we'll suggest industries,
                geographies and
                <br />
                the companies to go after.
              </p>
            </div>

            {/* Step icons + Saved Searches button */}
            <div className="d-flex align-items-center col-md-12 mb-3">
              <div
                className="col-md-6 offset-md-3"
                style={{ placeItems: "center" }}
              >
                <ul className="nav nav-tabs twitter-bs-wizard-nav justify-content-center">
                  {steps.map((s, i) => (
                    <li
                      className={`nav-item ${i <= 1 ? "me-sm-5" : "me-sm-3"}`}
                      key={s.id}
                    >
                      <button
                        type="button"
                        className={`nav-link border primary-btn-hover radiusBorder-45 border-dark ${
                          step === s.id ? "active bg-primary text-white" : ""
                        }`}
                        onClick={() => setStep(s.id)}
                      >
                        {errors.industries && s.id === 1 && (
                          <i className="fa-solid fa-asterisk fs-6 float-end text-danger"></i>
                        )}
                        <div className="step-icon">
                          <i className={s.icon}></i>
                        </div>
                        <small>{s.label}</small>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-md-3">
                <button
                  className="btn btn-secondary float-end"
                  onClick={openSavedSearches}
                >
                  <i className="fa-solid fa-clock-rotate-left me-1"></i> Saved
                  Target Lists
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <form>
                  {/* ── STEP 1: INDUSTRIES ───────────────────────────────── */}
                  {step === 1 && (
                    <>
                      <div className="row text-center mb-2">
                        <h2>Define Your Target List</h2>
                        <p className="fs-19 fw-normal text-gray">
                          Describe or mention the industries and geographies you
                          are looking to target.
                        </p>
                      </div>

                      {/* Role description */}
                      <div className="pricing-content rounded bg-light mb-3">
                        <div className="price-hdr">
                          <h6 className="fs-14 fw-medium w-100">
                            <i className="fa-solid fa-target ms-1 text-primary"></i>{" "}
                            Your target brief
                          </h6>
                        </div>
                        <input
                          type="hidden"
                          {...register("industries", {
                            validate: (v) =>
                              v.length > 0 || "Select at least one industry",
                          })}
                        />
                        <textarea
                          className="form-control"
                          rows="3"
                          placeholder="List of Tech services companies in South East Asia"
                          value={generatedRoleDescription || ""}
                          onChange={(e) =>
                            setGeneratedRoleDescription(e.target.value)
                          }
                        ></textarea>
                        <button
                          type="button"
                          className="btn btn-outline-secondary mt-1 ms-1"
                          onClick={suggestIndustries}
                          disabled={JdLoader}
                        >
                          <i className="fa-solid fa-wand-magic-sparkles me-1"></i>
                          Suggest Industries & Geographies
                          {JdLoader && (
                            <span
                              className="spinner-border spinner-border-sm ms-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          )}
                        </button>
                      </div>

                      <div className="row">
                        <hr style={{ opacity: 1 }} />
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="text-start">
                            <h6 className="fs-14 fw-medium w-100">
                              Select industries manually
                              {errors.industries && (
                                <small className="text-danger d-block ms-2 fs-10 mt-1">
                                  <i>{`[${errors.industries.message}]`}</i>
                                </small>
                              )}
                            </h6>
                          </div>
                          <div className="text-end">
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={() => goToStep(2)}
                            >
                              Next{" "}
                              <i className="fa-solid fa-chevron-right ms-1"></i>
                            </button>
                          </div>
                        </div>

                        {/* Selected chips */}
                        {updatedIndustries.map((id) => (
                          <div
                            key={id}
                            className="col-md-1 chip bg-primary mt-1 mb-1 ms-1"
                            onClick={() => toggle(id)}
                          >
                            {industryIdMap[id]}
                            <span className="chip-closebtn">&times;</span>
                          </div>
                        ))}

                        {/* Industry accordion */}
                        <div className="chat-search search-wrap contact-search visible-chat mt-2 mb-2">
                          <div className="border rounded p-3">
                            <div className="mb-3">
                              <span
                                style={{
                                  position: "absolute",
                                  right: "50px",
                                  marginTop: "8px",
                                }}
                              >
                                <i className="fa-solid fa-magnifying-glass"></i>
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search industries..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                              />
                            </div>
                            <div className="row border rounded p-2 g-2">
                              <div className="accordion" id="industryAccordion">
                                {filteredIndustries.length ? (
                                  filteredIndustries.map((industry, i) => {
                                    const allSubs = getAllSubsFromMains(
                                      industry.mains,
                                    );
                                    const selectedCount =
                                      getSelectedCount(allSubs);
                                    const totalCount = allSubs.length;
                                    return (
                                      <div
                                        className="accordion-item"
                                        key={industry.label}
                                      >
                                        <h2 className="d-flex flex-sm-row-reverse align-items-center">
                                          <button
                                            className="accordion-button collapsed"
                                            type="button"
                                            data-bs-toggle="collapse"
                                            data-bs-target={`#lvl1-${i}`}
                                          >
                                            <i className="fa-solid fa-chevron-right me-sm-2 arrow-icon"></i>
                                            {industry.label}
                                          </button>
                                          <span
                                            className={`fs-12 fw-medium ${totalCount === 0 ? "text-muted" : "text-primary"}`}
                                            style={{
                                              position: "absolute",
                                              zIndex: "999",
                                              marginRight: "10px",
                                            }}
                                          >
                                            ({selectedCount}/{totalCount})
                                          </span>
                                          <span
                                            className="text-primary fs-12 fw-medium ms-2 width-100"
                                            role="button"
                                            onClick={() =>
                                              toggleMajor(industry.mains)
                                            }
                                            style={{
                                              position: "absolute",
                                              zIndex: "999",
                                              marginRight: "20px",
                                            }}
                                          >
                                            {isAllMajorSelected(industry.mains)
                                              ? "Unselect all"
                                              : "Select all"}
                                          </span>
                                        </h2>
                                        <div
                                          id={`lvl1-${i}`}
                                          className={`accordion-collapse collapse ${search ? "show" : ""}`}
                                          data-bs-parent="#industryAccordion"
                                        >
                                          <div className="accordion-body p-2">
                                            {industry.mains.map((main, j) => (
                                              <div
                                                className="accordion mb-2"
                                                key={main.label}
                                              >
                                                <div className="accordion-item">
                                                  <div className="d-flex flex-sm-row-reverse align-items-center">
                                                    <button
                                                      className="accordion-button collapsed fs-14 flex-grow-1"
                                                      type="button"
                                                      data-bs-toggle="collapse"
                                                      data-bs-target={`#lvl2-${i}-${j}`}
                                                    >
                                                      <i className="fa-solid fa-chevron-right me-sm-2 arrow-icon1"></i>
                                                      {main.label}
                                                    </button>
                                                    <span
                                                      className={`fs-12 fw-medium ${main.subs.length === 0 ? "text-muted" : "text-primary"}`}
                                                      style={{
                                                        position: "absolute",
                                                        zIndex: "999",
                                                      }}
                                                    >
                                                      (
                                                      {
                                                        main.subs.filter((s) =>
                                                          selectedIndustries.includes(
                                                            s.id,
                                                          ),
                                                        ).length
                                                      }
                                                      /{main.subs.length})
                                                    </span>
                                                    <span
                                                      className="text-primary fs-12 fw-medium ms-2 width-100"
                                                      role="button"
                                                      onClick={() =>
                                                        toggleMain(main.subs)
                                                      }
                                                      style={{
                                                        position: "absolute",
                                                        zIndex: "999",
                                                      }}
                                                    >
                                                      {isAllSelected(main.subs)
                                                        ? "Unselect all"
                                                        : "Select all"}
                                                    </span>
                                                  </div>
                                                  <div
                                                    id={`lvl2-${i}-${j}`}
                                                    className={`accordion-collapse collapse ${search ? "show" : ""}`}
                                                  >
                                                    <div className="accordion-body">
                                                      <div className="row g-2">
                                                        {main.subs.map(
                                                          (sub, k) => (
                                                            <div
                                                              className="col-md-6"
                                                              key={k}
                                                            >
                                                              <label className="d-flex align-items-center gap-2 cursor-pointer">
                                                                <input
                                                                  type="checkbox"
                                                                  checked={selectedIndustries?.includes(
                                                                    sub.id,
                                                                  )}
                                                                  onChange={() =>
                                                                    toggle(
                                                                      sub.id,
                                                                    )
                                                                  }
                                                                />
                                                                <span>
                                                                  {sub.label}
                                                                </span>
                                                              </label>
                                                            </div>
                                                          ),
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <div className="text-muted text-center">
                                    No results found
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="text-end">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => goToStep(2)}
                        >
                          Next{" "}
                          <i className="fa-solid fa-chevron-right ms-1"></i>
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── STEP 2: GEOGRAPHIES ──────────────────────────────── */}
                  {step === 2 && (
                    <>
                      <div className="d-flex justify-content-between align-items-center text-center mb-2">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setStep(1)}
                        >
                          <i className="fa-solid fa-chevron-left me-1"></i>{" "}
                          Previous
                        </button>
                        <span>
                          <h2>Select Contact Geographies</h2>
                          <p className="fs-19 fw-normal text-gray">
                            Select entire regions or specific countries
                          </p>
                        </span>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => goToStep(3)}
                        >
                          Next{" "}
                          <i className="fa-solid fa-chevron-right ms-1"></i>
                        </button>
                      </div>

                      <div className="pricing-content rounded mb-3">
                        <div className="price-hdr">
                          <h6 className="fs-14 fw-medium w-100">
                            <i className="fa-solid fa-globe ms-1"></i> Quick
                            Select by Region
                          </h6>
                        </div>
                        <input
                          type="hidden"
                          {...register("countries", {
                            validate: (v) => {
                              if (v.length === 0) {
                                window.scrollTo(0, 0);
                                return "Select at least one country";
                              }
                            },
                          })}
                        />
                        <div className="row">
                          {dataregions &&
                          Object.keys(dataregions).length > 0 ? (
                            Object.keys(dataregions).map((regionName) => (
                              <div
                                key={regionName}
                                className="col-md-1 chip bg-primary mt-1 mb-1 ms-1"
                                onClick={() => toggleRegion(regionName)}
                              >
                                {regionName}
                              </div>
                            ))
                          ) : (
                            <>No Region Found</>
                          )}
                        </div>
                      </div>

                      <div className="row">
                        <hr style={{ opacity: 1 }} />
                        <h6 className="fs-14 fw-medium w-100 d-flex">
                          {updatedCountries.length > 0
                            ? "Selected countries"
                            : "Or select specific countries"}
                          {errors.countries && (
                            <small className="text-danger d-block ms-2 fs-10 mt-1">
                              <i>{`[${errors.countries.message}]`}</i>
                            </small>
                          )}
                        </h6>
                        {updatedCountries.length > 0 && (
                          <div className="row">
                            {updatedCountries.map((item) => (
                              <div
                                key={item}
                                className="col-md-1 chip bg-primary mt-1 mb-1 ms-1"
                                onClick={() => toggleCountries(item)}
                              >
                                {item}
                                <span className="chip-closebtn">&times;</span>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="chat-search search-wrap contact-search visible-chat mt-2 mb-2">
                          <div
                            className="border rounded p-3"
                            style={{ maxHeight: "500px", overflow: "overlay" }}
                          >
                            <div className="mb-3">
                              <span
                                style={{
                                  position: "absolute",
                                  right: "50px",
                                  marginTop: "8px",
                                }}
                              >
                                <i className="fa-solid fa-magnifying-glass"></i>
                              </span>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="Search locations..."
                                value={searchCountry}
                                onChange={(e) =>
                                  setSearchCountry(e.target.value)
                                }
                              />
                            </div>
                            {filteredCountriesByRegion.length ? (
                              filteredCountriesByRegion.map((group, index) => (
                                <div key={index} className="mb-3">
                                  <h6 className="fw-semibold text-primary mb-3">
                                    {group.region}
                                  </h6>
                                  <div className="row border rounded p-2 g-2">
                                    {group.countries.map((item, i) => (
                                      <div className="col-md-6" key={i}>
                                        <label className="d-flex align-items-center gap-2 p-2 div-focus cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={selectedCountries?.includes(
                                              item.label,
                                            )}
                                            onChange={() =>
                                              toggleCountries(item.label)
                                            }
                                          />
                                          <span>{item.label}</span>
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="text-muted text-center">
                                No results found
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-between">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setStep(1)}
                        >
                          <i className="fa-solid fa-chevron-left me-1"></i>{" "}
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => goToStep(3)}
                        >
                          Next{" "}
                          <i className="fa-solid fa-chevron-right ms-1"></i>
                        </button>
                      </div>
                    </>
                  )}

                  {/* ── STEP 3: COMPANIES ────────────────────────────────── */}
                  {step === 3 && (
                    <>
                      <div className="d-flex justify-content-between align-items-center text-center mb-2">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setStep(2)}
                        >
                          <i className="fa-solid fa-chevron-left me-1"></i>{" "}
                          Previous
                        </button>
                        <h2>Companies</h2>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={openSaveModal}
                        >
                          <i className="fa-solid fa-floppy-disk me-1"></i> Save
                          List
                        </button>
                      </div>

                      <div className="row">
                        <hr style={{ opacity: 1 }} />

                        {/* Filters row */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div
                            className="d-flex align-items-center gap-2 flex-wrap"
                            style={{ minWidth: "50%" }}
                          >
                            {/* Company name search */}
                            <div
                              className="d-flex align-items-center"
                              style={{ minWidth: "100%" }}
                            >
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                placeholder="Search companies (comma separated)..."
                                value={companyInput}
                                onChange={(e) =>
                                  setCompanyInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleCompanySearch();
                                  }
                                }}
                              />
                              {isSearchActive && (
                                <button
                                  className="btn btn-sm btn-outline-secondary ms-1"
                                  onClick={clearCompanySearch}
                                  title="Clear search"
                                >
                                  <i className="fa-solid fa-xmark"></i>
                                </button>
                              )}
                            </div>

                            {/* Name chips */}
                            {companyNames.map((name, idx) => (
                              <span
                                key={idx}
                                className="chip bg-primary text-white d-flex align-items-center"
                              >
                                {name}
                                <span
                                  className="chip-closebtn ms-2"
                                  role="button"
                                  onClick={() => removeCompanyName(name)}
                                >
                                  &times;
                                </span>
                              </span>
                            ))}
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <span className="text-muted fs-13">Filter:</span>
                            <div style={{ minWidth: "160px" }}>
                              <Select
                                options={cr_options}
                                isMulti
                                placeholder="Revenue"
                                classNamePrefix="react-select"
                                onChange={handleRevenueChange}
                                value={cr_options.filter((o) =>
                                  revenueFilter.includes(o.value),
                                )}
                              />
                            </div>
                            <div style={{ minWidth: "140px" }}>
                              <Select
                                options={sz_options}
                                isMulti
                                placeholder="Size"
                                classNamePrefix="react-select"
                                onChange={handleSizeChange}
                                value={sz_options.filter((o) =>
                                  sizeFilter.includes(o.value),
                                )}
                              />
                            </div>
                            <button
                              className="btn btn-outline-primary btn-sm"
                              onClick={(e) => {
                                e.preventDefault();
                                applyFilters();
                              }}
                            >
                              Apply
                            </button>
                            {(revenueFilter.length > 0 ||
                              sizeFilter.length > 0) && (
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={resetFilters}
                              >
                                Reset
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-normal fs-13">
                            Showing:{" "}
                            {companies.length > 0
                              ? `${currentPage * 50 + 1}–${Math.min((currentPage + 1) * 50, totalCompanies)}`
                              : 0}{" "}
                            of {totalCompanies}
                            {selectedCompanies.length > 0 && (
                              <span className="ms-2 badge bg-primary">
                                {selectedCompanies.length} selected
                              </span>
                            )}
                          </span>
                          {isResumed && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                setIsResumed(false);
                                get_companies(0, appliedFilters);
                              }}
                              disabled={loading}
                            >
                              <i className="fa-solid fa-rotate me-1"></i>
                              Show All Companies
                            </button>
                          )}
                        </div>

                        {/* Companies table */}
                        <div className="table-responsive border rounded">
                          <table className="table table-hover align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th style={{ width: "40px" }}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={
                                      displayedCompanies.length > 0 &&
                                      displayedCompanies.every((c) =>
                                        isCompanySelected(c),
                                      )
                                    }
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        const newSelections =
                                          displayedCompanies.filter(
                                            (c) => !isCompanySelected(c),
                                          );
                                        setSelectedCompanies((prev) => [
                                          ...prev,
                                          ...newSelections,
                                        ]);
                                      } else {
                                        const displayedIds =
                                          displayedCompanies.map((c) => c.id);
                                        setSelectedCompanies((prev) =>
                                          prev.filter(
                                            (c) => !displayedIds.includes(c.id),
                                          ),
                                        );
                                      }
                                    }}
                                    title="Select / Deselect all on this page"
                                  />
                                </th>
                                <th>Company</th>
                                <th>Website</th>
                                <th className="text-end">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                <tr>
                                  <td colSpan={4} className="text-center py-4">
                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                    Loading companies...
                                  </td>
                                </tr>
                              ) : displayedCompanies.length > 0 ? (
                                displayedCompanies.map((company, i) => (
                                  <tr
                                    key={company.id || i}
                                    className={
                                      isCompanySelected(company)
                                        ? "table-primary"
                                        : ""
                                    }
                                  >
                                    <td>
                                      <input
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={isCompanySelected(company)}
                                        onChange={() =>
                                          toggleCompanySelection(company)
                                        }
                                      />
                                    </td>
                                    <td>
                                      <div className="d-flex align-items-center gap-2">
                                        <img
                                          src={
                                            company.logoUrl ||
                                            `${PINE_BASE_URL}/files/companyLogo/nologo.png`
                                          }
                                          alt={company.name}
                                          className="avatar avatar-sm avatar-rounded border"
                                          style={{
                                            width: "36px",
                                            height: "36px",
                                            objectFit: "contain",
                                          }}
                                        />
                                        <span className="fw-medium">
                                          {company.name}
                                        </span>
                                      </div>
                                    </td>
                                    <td>
                                      {company.fqdn ? (
                                        <a
                                          href={`//${company.fqdn}`}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="fs-13 text-muted"
                                        >
                                          <i className="fa-solid fa-globe me-1"></i>
                                          {company.fqdn}
                                        </a>
                                      ) : (
                                        <span className="text-muted fs-13">
                                          N/A
                                        </span>
                                      )}
                                    </td>
                                    <td className="text-end">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setAddProspectCompany({
                                            name: company.name,
                                            fqdn: company.fqdn,
                                          });
                                          setAddProspect(true);
                                        }}
                                        className="btn btn-sm btn-outline-dark me-2"
                                      >
                                        <i className="fa-solid fa-plus"></i> Add
                                        Prospect
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() =>
                                          setExecutivesModal({
                                            fqdn: company.fqdn,
                                            name: company.name,
                                          })
                                        }
                                        disabled={!company.fqdn}
                                      >
                                        <i className="fa-solid fa-users me-1"></i>{" "}
                                        Show Executives
                                      </button>
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={4}
                                    className="text-center text-muted py-4"
                                  >
                                    No companies found. Adjust your filters or
                                    go back to change industries/geographies.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              disabled={currentPage === 0}
                              onClick={() => handlePageChange(currentPage - 1)}
                            >
                              <i className="fa-solid fa-chevron-left"></i>
                            </button>
                            <span className="fs-13">
                              Page {currentPage + 1} of {totalPages}
                            </span>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              disabled={currentPage >= totalPages - 1}
                              onClick={() => handlePageChange(currentPage + 1)}
                            >
                              <i className="fa-solid fa-chevron-right"></i>
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="d-flex justify-content-between mt-3">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setStep(2)}
                        >
                          <i className="fa-solid fa-chevron-left me-1"></i>{" "}
                          Previous
                        </button>
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={openSaveModal}
                        >
                          <i className="fa-solid fa-floppy-disk me-1"></i> Save
                          List
                        </button>
                      </div>
                    </>
                  )}
                </form>

                {addProspect && (
                  <AddProspect
                    onClose={async (id) => {
                      setAddProspect(false);
                      setAddProspectCompany(null);
                      if (id) {
                        setShowExistingClient(true);
                        setExistingClientId(id);
                      }
                    }}
                    prefillName={addProspectCompany?.name}
                    prefillWebsite={addProspectCompany?.fqdn}
                  />
                )}

                {ShowExistingClient && (
                  <ExistingClient
                    onClose={() => {
                      setShowExistingClient(false);
                    }}
                    company_id={ExistingClientId}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── EXECUTIVES MODAL ────────────────────────────────────────────────── */}
      {executivesModal && (
        <ExecutivesModalProspecting
          fqdn={executivesModal.fqdn}
          companyName={executivesModal.name}
          onClose={() => setExecutivesModal(null)}
        />
      )}

      {/* ── SAVE SEARCH MODAL ───────────────────────────────────────────────── */}
      {showSaveModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fa-solid fa-floppy-disk me-2 text-primary"></i>
                    Save List
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSaveModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="text-muted fs-13 mb-3">
                    {selectedCompanies.length > 0
                      ? `Saving ${selectedCompanies.length} selected company(s).`
                      : `No companies selected — all ${companies.length} displayed companies will be saved.`}
                  </p>
                  <div className="mb-3">
                    <label className="form-label fw-medium">
                      Search Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Healthcare VPs in APAC"
                      value={saveSearchName}
                      onChange={(e) => setSaveSearchName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveSearch();
                      }}
                    />
                    <small className="text-muted">
                      Name must be unique across your Saved Target List.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary me-1"
                    onClick={() => setShowSaveModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveSearch}
                    disabled={savingSearch}
                  >
                    {savingSearch ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1"></span>{" "}
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-floppy-disk me-1"></i> Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* ── SAVED SEARCHES MODAL ────────────────────────────────────────────── */}
      {showSavedSearchesModal && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fa-solid fa-clock-rotate-left me-2 text-primary"></i>
                    Saved Target List
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSavedSearchesModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  {savedSearchesLoading ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm me-2"></span>{" "}
                      Loading...
                    </div>
                  ) : savedSearches.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      No Saved Target List found. Use "Save List" on the
                      Companies step to save one.
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Name</th>
                            <th>Industries</th>
                            <th>Countries</th>
                            <th>Companies Saved</th>
                            <th>Date</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {savedSearches.map((s) => (
                            <tr key={s.id}>
                              <td className="fw-medium">{s.name}</td>
                              <td>
                                <span className="badge bg-light text-dark">
                                  {(s.industries || []).length} industries
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-light text-dark">
                                  {(s.countries || []).length} countries
                                </span>
                              </td>
                              <td>
                                <span className="badge bg-primary">
                                  {(s.selected_companies || []).length}{" "}
                                  companies
                                </span>
                              </td>
                              <td className="text-muted fs-12">
                                {new Date(s.created_at).toLocaleDateString()}
                              </td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => resumeSearch(s)}
                                >
                                  <i className="fa-solid fa-play me-1"></i>{" "}
                                  Resume
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}
